import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { getNotificationNavigationUrl } from '@/utils/notificationNavigation';
import { logger } from '@/utils/logger';
import { NotificationBanner, type BannerNotification } from './NotificationBanner';

type NotificationRow = {
  id: string;
  title: string;
  message: string;
  type: string;
  data: Record<string, unknown> | null;
  read: boolean;
};

/**
 * Announces a notification that arrives while the app is on screen.
 *
 * This is the third of the three states in 20260910120000_chat_presence.sql. Reading the
 * thread gets you nothing, having the app shut gets you a push, and this is the middle
 * one: the fan-out withheld the push precisely because your client said it was awake, so
 * without a banner the message lands in the inbox in total silence.
 *
 * Chat rows arrive as INSERT the first time and UPDATE after that -- they collapse into
 * one row per thread -- so both are watched. `read` is what separates a new message from
 * the UPDATE that marks a row read: the former leaves it false, the latter sets it true.
 */
export function NotificationBannerHost({ userId }: { userId: string | undefined }) {
  const [current, setCurrent] = useState<(BannerNotification & { data: Record<string, unknown> | null }) | null>(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!userId) {
      setCurrent(null);
      return;
    }

    const show = (payload: { new: Record<string, unknown> }) => {
      const row = payload.new as unknown as NotificationRow;
      if (!row?.id || row.read) return;
      // Hidden tab: the push already covered this, or will. A banner nobody is looking
      // at would just be waiting on screen when they come back, out of date.
      if (document.visibilityState !== 'visible') return;

      // Newest wins. Stacking banners would cover the app during a busy exchange.
      setCurrent({
        id: row.id,
        title: row.title,
        message: row.message,
        type: row.type,
        data: row.data,
      });
    };

    const channel = supabase
      .channel(`notification-banner:${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        show
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        show
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId]);

  if (!current) return null;

  return (
    <NotificationBanner
      notification={current}
      onOpen={() => {
        const url = getNotificationNavigationUrl({ type: current.type, data: current.data });
        if (url) navigateRef.current(url);
        // Same as tapping the push: acting on it means it has been seen. A chat row is
        // also cleared by mark_chat_notifications_read when the thread opens, so this
        // mainly covers the types that do not lead to a chat.
        void supabase
          .from('notifications')
          .update({ read: true })
          .eq('id', current.id)
          .then(({ error }) => {
            if (error) logger.error('NotificationBannerHost mark read failed', error);
          });
      }}
      onClose={() => setCurrent(null)}
    />
  );
}
